import React, {useState} from "react";
import '../../scss/components/ProfileSettings.scss';
import '../../scss/components/ProfileImage.scss';
import PasswordInput from '../Form/PasswordInput.tsx';
import { useProfileApi } from "../../features/auth/hooks/use-profile-api.ts";
import { ApiResponse } from "../../interface/ApiResponse.ts";
import { ApiResponseType } from "../../features/api/types.ts";
import Form from '../Form/Form.tsx';
import Button from "../Form/Button.tsx";
import TextInput from '../Form/TextInput.tsx';
import { IProfile } from "../../interface/IProfile.ts";
import { ITotpSecret } from "../../interface/ITotpSecret.ts";
import PasswordInputWithValidation from "../Form/PasswordInputWithValidation.tsx";
import QRCode from "react-qr-code";

export default function ProfileSettings({
    profile,
    setProfile,
}: {
    profile: IProfile,
    setProfile: (profile:IProfile) => void
}) {
    const [errors, setErrors] = useState<Array<String>>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const [passwordSubmitting, setPasswordSubmitting] = useState(false);
    const [responseMessage, setResponseMessage] = useState('');
    const [responseMessageClass, setResponseMessageClass] = useState<string>('');
    const [totpResponseMessage, setTotpResponseMessage] = useState('');
    const [totpResponseMessageClass, setTotpResponseMessageClass] = useState<string>('');
    const { updatePassword, getTotpSecret, activateTotp, deactivateTotp } = useProfileApi();
    const [totpSecret, setTotpSecret] = useState<ITotpSecret|null>();
    const [totpCode, setTotpCode] = useState<string>('');
        
    const [unSavedChanges, setUnSavedChanges] = useState(false);

    const [formData, setFormData] = useState<any>({
        currentpassword: '',
        newpassword: '',
        repeatpassword: ''
    });

    const [passwordCheckPassed, setPasswordCheckPassed] = useState<boolean>(false);


    const handleFormChange = (inputFormData, inputPasswordCheckPassed) => {
        let formDataClone = structuredClone(formData);
        formDataClone = {...formDataClone, ...inputFormData};
        
        setPasswordCheckPassed(inputPasswordCheckPassed);
        setFormData(formDataClone);
        !unSavedChanges && setUnSavedChanges(true);
    }
    

    const handleChange = (event, name = '', value = null) => {
        let formDataClone = structuredClone(formData);
        formDataClone = { ...formDataClone, [name ? name : event.target.name]: (value ? value : event.target.value) }

        setFormData(formDataClone);
        !unSavedChanges && setUnSavedChanges(true);
    };

    const handleSubmit = (event) => {
        event.preventDefault();

        setResponseMessage('');
        setErrors([]);

        if (formData.newpassword !== formData.repeatpassword) {
            setResponseMessage('Neue Passwörter stimmen nicht überein');
            setResponseMessageClass('error');
            return;
        }

        setPasswordSubmitting(true);
        setUnSavedChanges(false);
        setIsLoading(true);

        updatePassword(formData.currentpassword, formData.newpassword).then((res:ApiResponse) => {
            if (res.type === ApiResponseType.CLIENT_ERROR || res.type === ApiResponseType.SERVER_ERROR || res.type === ApiResponseType.FORM_INVALID) {
                setResponseMessage(res.payload.error);
                setResponseMessageClass('error');
                
            } else if (res.type === ApiResponseType.OK) {
                setResponseMessage('Passwort geändert');
                setResponseMessageClass('success');
            }
            setIsLoading(false);
            setPasswordSubmitting(false);
        });
    }

    const handleTotpSubmit = (event) => {
        event.preventDefault();
        setIsLoading(true);
        activateTotp(totpCode).then((res:ApiResponse) => {
            if (res.type === ApiResponseType.CLIENT_ERROR || res.type === ApiResponseType.SERVER_ERROR || res.type === ApiResponseType.FORM_INVALID) {
                setTotpResponseMessage(res.payload.error);
                setTotpResponseMessageClass('error');
            } else if (res.type === ApiResponseType.OK) {
                setTotpResponseMessage('Zwei-Faktor-Authentifizierung aktiviert');
                setTotpResponseMessageClass('success');
                let profileData = { ...profile };
                profileData.is_totp_enabled = 1;
                setProfile(profileData);
            }
            setIsLoading(false);
        });
    }

    return (
        <>
            <h2 className="h5">Zwei-Faktor-Authentifizierung (2FA)</h2>
            { profile.is_totp_enabled == 1 ? 
                <div className="two-fa-settings-container">
                    <p>Die Zwei-Faktor-Authentifizierung für Ihr FPSB-Konto ist aktiviert.</p>
                    <Button
                        buttonType='filled-danger'
                        type='submit'
                        onClick={(e) => {
                            e.preventDefault();
                            deactivateTotp().then((res:ApiResponse) => {
                                if (res.type === ApiResponseType.CLIENT_ERROR || res.type === ApiResponseType.SERVER_ERROR || res.type === ApiResponseType.FORM_INVALID) {
                                    setTotpResponseMessage(res.payload.error);
                                    setTotpResponseMessageClass('error');
                                } else if (res.type === ApiResponseType.OK) {
                                    let profileData = { ...profile };
                                    profileData.is_totp_enabled = 0;
                                    setProfile(profileData);
                                    setTotpSecret(null);
                                }
                                setIsLoading(false);
                            });
                        }}
                    >Zwei-Faktor-Authentifizierung entfernen</Button>
                </div> :
                <div className="two-fa-settings-container">
                    <p>Aktivieren Sie als zusätzliche Sicherheitsebene für Ihr FPSB-Konto die Zwei-Faktor-Authentifizierung. Sie können jede Authentifizierung-App, wie etwa Google Authenticator, Microsoft Authenticator, Authy oder LastPass Authenticator, verwenden.</p>
                    <p>Die Zwei-Faktor-Authentifizierung für Ihr FPSB-Konto ist nicht aktiviert.</p>
                    { totpSecret ? 
                        <Form
                            className="totp-container"
                            id="profile-2fa-form" 
                            customSubmit
                            handleSubmit={handleTotpSubmit}
                            isLoading={isLoading}
                            responseMessage={totpResponseMessage}
                            responseMessageClass={totpResponseMessageClass}
                        >
                            <p>Bitte scannen Sie den QR-Code mit Ihrer Authentifizierungs-App ab. Geben Sie anschließend den angezeigten, sechsstellingen Code zur Bestätigung ein.</p>
                            <div className="qr-code"><QRCode value={ totpSecret.url ?? '' } /></div>
                            <p><strong>Code:</strong> { totpSecret.token }</p>
                            <TextInput
                                onChange={ (event) => {
                                    setTotpCode(event.target.value);
                                } }
                                value={totpCode}
                                name="totp_code"
                                required={true}
                            />
                            <Button
                                buttonType='primary'
                                type='submit'
                            >Code verifizieren</Button>
                        </Form>
                        :
                        <Button
                            buttonType='primary'
                            type='submit'
                            onClick={(e) => {
                                e.preventDefault();
                                getTotpSecret().then(data => {
                                    setTotpSecret(data.payload as ITotpSecret);
                                });
                            }}
                        >Zwei-Faktor-Authentifizierung hinzufügen</Button>
                    }
                </div>
            }
            <Form 
                id="profile-settings-form" 
                className={(passwordSubmitting) ? ' loading' : ''}
                unSavedChanges={unSavedChanges}
                handleSubmit={handleSubmit}
                isLoading={isLoading}
                responseMessage={responseMessage}
                responseMessageClass={responseMessageClass}
                disabled={formData.currentpassword.length <= 0 || !passwordCheckPassed}
            >
                <h2 className="h5">Passwort ändern</h2>
                <PasswordInput
                    onChange={handleChange}
                    value={formData.currentpassword}
                    placeholder='Aktuelles Passwort'
                    name='currentpassword'
                    label='Aktuelles Passwort'
                    showToggle={true}
                    autocomplete='current-password'
                />
                <PasswordInputWithValidation 
                    outerChangeHandler={handleFormChange}
                    additionalHintUpper={false}
                />

                { errors.map((el, key) => (
                    <div className='message error' role='alert' key={key}>
                        <span>{ el }</span>
                    </div>
                )) }
            </Form>
        </>
    )
}
